/**
 * Personalized distribution: the guidance copy the UI shows. Steps rail tips, step headers,
 * "How it works", the examples, the list's empty state, row actions and the shared labels.
 *
 * Copy follows the prototype's contract: sentence case, plain words, active voice, no emoji.
 * Errors say what's wrong and how to fix it. The UI never says "burst".
 *
 * Pure data plus a few tiny fillers for copy that names the split field or a count, so every
 * string the list, rail and frame render comes from one place and is unit-tested.
 */

import type {
  AudienceApi,
  DistributionCheckCodeApi,
  SensitivityClassApi,
  TileKindApi,
} from "../services/distributions/distributions.types";

export type DistributionStepId =
  | "split"
  | "recipients"
  | "content"
  | "message"
  | "schedule"
  | "review";

export type DistributionStepNumber = 1 | 2 | 3 | 4 | 5 | 6;

export const DISTRIBUTION_STEP_COUNT = 6;

export interface DistributionStepHelp {
  id: DistributionStepId;
  number: DistributionStepNumber;
  /** Rail label. */
  label: string;
  /** The panel title when it differs from the rail label. */
  title?: string;
  /** One line under the panel title; empty shows none. `{field}` is replaced with the split field's label. */
  description: string;
  /** "What this step does", shown under the current step in the rail. */
  tip: string;
  /** What the step covers; the placeholder body lists it until the step organism plugs in. */
  covers: readonly string[];
}

export const DISTRIBUTION_STEPS_HELP: readonly DistributionStepHelp[] = [
  {
    id: "split",
    number: 1,
    label: "Split by",
    description: "Who the copies are for, and which field cuts the story into copies.",
    tip: "Pick the field that decides who sees what. Each value you keep becomes its own copy, filtered when the data is queried, so nobody sees another slice.",
    covers: [
      "Distribution name and the field to split by, with its description and source",
      "The values to include, with rows and sales for each and the filter each copy uses",
      "Which tiles personalize, which are left out and why, and notes or links you can send as they are",
      "Rules for values with no data and for new values",
    ],
  },
  {
    id: "recipients",
    number: 2,
    label: "Recipients",
    description:
      "Add one row per person with the {field} they should get. Every row is checked before anything is sent.",
    tip: "Upload one row per person with the value they should get. Every row is checked, and anything to fix is shown here before anything is sent.",
    covers: [
      "Upload a CSV, or add people one row at a time",
      "Columns matched automatically, with a template to start from",
      "Coverage for every value, and what happens to a value nobody receives",
      "Every row checked, with a fix message for each problem",
    ],
  },
  {
    id: "content",
    number: 3,
    label: "Content",
    title: "Content & branding",
    description: "",
    tip: "Choose formats, template and theme, then preview exactly what one person gets.",
    covers: [
      "Formats (PDF, PowerPoint), template and deck theme, with a default logo and colour that recipient rows can override",
      "AI summary and inline charts on or off",
      "Files attached to each copy",
      "Preview as a recipient, and send a test to yourself",
    ],
  },
  {
    id: "message",
    number: 4,
    label: "Message",
    description: "The email and Slack text that goes with each copy.",
    tip: "Write the email subject and body, and the Slack text. Variables such as {{name}} are filled in for each person.",
    covers: [
      "Email subject and body, and Slack text, with variable chips such as {{name}} and {{team}}",
      "Turn the message off to send files only",
      "Preview as a recipient, and send a test to yourself",
    ],
  },
  {
    id: "schedule",
    number: 5,
    label: "Schedule",
    description: "When the copies go out.",
    tip: "Send now, once, or on a repeat, and decide when it ends.",
    covers: [
      "Send now, once at a date and time, or repeat daily, weekly, monthly or quarterly",
      "Every N, weekdays, day of month, time and time zone",
      "Start date, and an end: never, on a date, or after N sends",
      "The next sends, with a note when daylight saving changes",
    ],
  },
  {
    id: "review",
    number: 6,
    label: "Review",
    title: "Review and activate",
    description: "",
    tip: "Read the plan in one sentence, fix anything flagged, then activate.",
    covers: [
      "One plain sentence describing the whole plan",
      "Checks, each with a Fix link back to the right step",
      "Activate, Activate & send now, and Save as draft",
    ],
  },
];

export const DISTRIBUTION_FEATURE_NAME = "Personalized distribution";

/** Replaces `{field}` with the split field's label, or "value" before one is chosen. */
export const fillFieldPlaceholder = (
  text: string,
  fieldLabel: string | null | undefined,
): string => text.split("{field}").join(fieldLabel?.trim() || "value");

export const getDistributionStepHelp = (
  step: DistributionStepNumber,
): DistributionStepHelp => DISTRIBUTION_STEPS_HELP[step - 1];

// ─── Activation checks ────────────────────────────────────────────────────────

/**
 * What each server check code means and which step fixes it. The server's `message` is shown
 * when it sends one (it can name counts and values); this text stands in when it doesn't, and the
 * step here is used when the server's step is missing or out of range.
 */
export const DISTRIBUTION_CHECK_HELP: Readonly<
  Record<DistributionCheckCodeApi, { step: DistributionStepNumber; text: string }>
> = {
  no_split: { step: 1, text: "Choose the field that splits the copies." },
  no_values: { step: 1, text: "Keep at least one value to split by." },
  no_recipients: { step: 2, text: "No one is added yet. Add at least one recipient." },
  recipients_to_fix: { step: 2, text: "Some recipient rows need a fix before you can activate." },
  uncovered_values: { step: 2, text: "Some values have no recipient, so no one gets those copies." },
  coverage_unknown: { step: 2, text: "The values couldn’t be listed, so we can’t tell if everyone is covered." },
  nothing_to_send: { step: 3, text: "Nothing goes out. Turn on the files in Content or the message in Message." },
  no_formats: { step: 3, text: "Choose PDF, PowerPoint or both in Content." },
  schedule_error: { step: 5, text: "The schedule couldn’t be set. Save it again in Schedule." },
};

/** For a code the UI doesn't know yet that also came without a message. */
export const DISTRIBUTION_CHECK_FALLBACK = "Something needs a fix before this can be activated.";

// ─── Wizard frame ─────────────────────────────────────────────────────────────

export const DISTRIBUTION_WIZARD_COPY = {
  railTitle: "Steps",
  tipHeading: "Tip",
  newDistributionTitle: "New distribution",
  untitledDraft: "Untitled distribution",
  cancel: "Cancel",
  back: "Back",
  saveExit: "Save & exit",
  continue: "Continue",
  activate: "Activate",
  /** Appended to any footer warning: problems never stop Continue, only activation. */
  continueLater: "You can continue and fix this later; it blocks activation.",
  autosaveIdle: "Your draft saves as you go",
  autosaveSaving: "Saving…",
  autosaveError: "We couldn’t save your last change.",
  notStarted: "Not started",
  messageOff: "Files only",
  messageEmail: "Email",
  messageSlack: "Slack",
  messageEmpty: "No message yet",
  noOneYet: "No one added yet",
  readyToActivate: "Ready to activate",
  active: "Active",
  fixTheSchedule: "Fix the schedule",
  sendNow: "Send now",
  placeholderHeading: "This step plugs in here",
  placeholderPreview: "The preview for this step plugs in here.",
  stepKicker: (step: number, total: number) => `Step ${step} of ${total}`,
  draftSaved: (when: string) => `Draft saved ${when}`,
} as const;

/** Continue's blocking messages. Problems never block Continue; these are the only stops. */
export const DISTRIBUTION_CONTINUE_ERRORS = {
  noAudience: "Choose who receives these copies to continue.",
  noDraft:
    "Add a distribution name and choose a field to split by to continue.",
  noName: "Add a distribution name to continue.",
  noValues: "Keep at least one value to continue.",
  /** Leaving a step saves its last edits first; this shows when that save gave no reason. */
  saveFailed: "Couldn’t save your last changes, so you’re still on this step. Try again.",
} as const;

// ─── Audience (step 1) ────────────────────────────────────────────────────────

/**
 * "Who receives these copies?": it sets what every copy masks. Each line reads the class that
 * audience's copies are redacted at for this owner (`clearances` from split-fields), never the
 * tenant defaults; until it loads, the line says the organisation's settings decide.
 * `organisation` is the tenant's name, null until it loads.
 */
const MASKED_AT: Readonly<Record<SensitivityClassApi, string>> = {
  public: "Only public values show. Internal and confidential values and personal data are masked.",
  internal: "Internal values show. Confidential values and personal data are masked.",
  confidential: "Confidential values show. Personal data is masked.",
  restricted_pii: "Every value shows, personal data included. Nothing is masked.",
};

export const DISTRIBUTION_AUDIENCE_COPY = {
  legend: "Who receives these copies",
  hint: "Copies never show more than you can see yourself.",
  inside: (organisation: string | null) => `People in ${organisation ?? "your organisation"}`,
  outside: (organisation: string | null) => `People outside ${organisation ?? "your organisation"}`,
  /** "people outside Acme", inside a sentence. */
  short: (audience: AudienceApi, organisation: string | null) =>
    `people ${audience === "inside" ? "in" : "outside"} ${organisation ?? "your organisation"}`,
  /** What each choice does, on its card (no tenant or class names: the rules are the same everywhere). */
  explain: {
    inside:
      "Each person gets the data their own access allows, sliced as you set. Addresses outside your organisation are refused.",
    outside:
      "One copy for everyone. Columns classified above the outside level, like confidential and PII, are masked.",
  },
  /** What an audience's copies show and mask, from the class they're redacted at. */
  detail: (clearance: SensitivityClassApi | null) =>
    clearance ? MASKED_AT[clearance] : "What’s masked follows your organisation’s settings.",
  /** The field list depends on the audience, so the select waits for it. */
  fieldPlaceholder: "Choose who receives these copies first",
} as const;

// ─── Round 6 · step 1 items (FE-items) ────────────────────────────────────────

/** Step 1's name field. The example is neutral: no split field or demo value in it. */
export const DISTRIBUTION_NAME_COPY = {
  label: "Distribution name",
  placeholder: "For example, Weekly team update",
  helper: "Only you and other editors see this name.",
  /** Under the field once Continue was pressed without a name. */
  required: "Add a name so you can find this distribution later.",
} as const;

/** Step 1's values when there are none to choose from, and the ways out. */
export const DISTRIBUTION_VALUES_STATE_COPY = {
  hiddenTitle: (field: string, audienceShort: string) => `${field} values are hidden from ${audienceShort}.`,
  failedTitle: (field: string) => `We couldn’t list the ${field} values.`,
  emptyTitle: (field: string) => `${field} has no values right now.`,
  emptyText: "There’s nothing to split. Choose another field, or check the field in the data model.",
  switchAudience: (audienceShort: string) => `Switch to ${audienceShort}`,
  retry: "Try again",
  chooseField: "Choose another field",
} as const;

/** "What each copy includes": one card per Story item, with its switch, status and reason. */
/** Step 3's exact preview and send test entry points (round 6: the header buttons and "Preview and test"). */
export const DISTRIBUTION_PREVIEW_TEST_COPY = {
  headerExact: "Exact preview",
  headerTest: "Send test",
  title: "Preview and test",
  exactTitle: "Exact preview",
  exactLead: (field: string) =>
    `Builds the real copy for one ${field}: every page of the file, the email and the Slack post, with masking applied. Takes a few seconds.`,
  exactValueLabel: (field: string) => `${field} to preview`,
  exactOpen: "Open exact preview",
  testTitle: "Send a test",
  testLead: "Send someone’s copy to yourself, other email addresses or a Slack channel. Tests are labelled and don’t count as sends.",
  testSend: "Send test",
  lastTest: (when: string, value: string, sentTo: string) => `Last test ${when}: the ${value} copy to ${sentTo}.`,
  noRecipients: "Add recipients in step 2 to preview or test someone’s copy.",
} as const;

export const DISTRIBUTION_ITEMS_COPY = {
  heading: "What each copy includes",
  reset: "Reset",
  listLabel: (storyTitle: string) => `Items in ${storyTitle}`,
  loading: "Checking which items can be filtered…",
  tally: (included: number, total: number) => `${included} of ${total} ${total === 1 ? "item" : "items"} in each copy`,
  filteredPill: (count: number, field: string) => `${count} filtered to each ${field}`,
  asIsPill: (count: number) => `${count} as is`,
  leftOutPill: (count: number) => `${count} left out`,
  maskedPill: (count: number) => `${count} masked`,
  figuresNote: (value: string) => `KPI figures show the ${value} copy.`,
  staysOut: (field: string) =>
    `Anything that can’t be filtered to one ${field} stays out, so nobody sees another ${field}’s numbers.`,
  noPersonalItem: (field: string) =>
    `Turn on at least one item that’s filtered to each ${field}. Otherwise every copy is the same.`,
  /** Continue's stop when every filtered item is off. */
  continueNoPersonalItem: (field: string) => `Turn on at least one item that’s filtered to each ${field} to continue.`,
  switchLabel: (title: string) => `Include ${title} in each copy`,
  kind: {
    kpi: "KPI",
    chart: "Chart",
    table: "Table",
    note: "Note",
    link: "Link",
    bi_view: "Embedded view",
    still: "Still image",
  } as Readonly<Record<TileKindApi, string>>,
  filtered: (field: string) => `Filtered to each ${field}`,
  maskedFor: (audience: string) => `Masked for ${audience}`,
  maskedNote: "Recipients see “Masked” instead of its sensitive values. Turn it off to leave it out.",
  maskedFigure: "Masked",
  turnedOff: "Turned off",
  turnedOffNote: "Left out of every copy.",
  asIs: "Included as is",
  asIsNote: {
    note: "Everyone sees the same note.",
    link: "Everyone gets the same link.",
    still: "Everyone sees the same picture.",
    other: "Everyone sees the same content.",
  },
  leftOut: "Left out",
  everyValueMasked: "Every value masked",
  cantBeFiltered: "Can’t be filtered",
  wouldShowEvery: (field: string) => `Would show every ${field}`,
  /** A turned-off item where left-out reasons show (run detail, template builder). */
  turnedOffTag: "Turned off",
  turnedOffShort: "Left out: turned off",
  /** Why a turned-off item is left out, where no server message exists yet (the template builder). */
  turnedOffReason: "Turned off in Split by, so it’s left out of every copy.",
} as const;

// ─── Copy preview ─────────────────────────────────────────────────────────────

export const DISTRIBUTION_COPY_PREVIEW_COPY = {
  masked: "Masked",
  maskedNote: "Sensitive",
  leftOut: "Left out",
  /** Every copy of a distribution masks at its audience's clearance (step 1), whoever receives it. */
  maskedEverywhere: "Sensitive values are masked in every copy.",
  onlyValue: (value: string) => `${value} only`,
  moreTiles: (count: number) =>
    `+ ${count} more ${count === 1 ? "tile" : "tiles"}`,
  leftOutCount: (count: number) => `${count} left out`,
  sentAsIs: (count: number) => `${count} as is`,
  trendLabel: (value: string) => `Trend for ${value}`,
  quickPreviewNote: (asOf: string | null) =>
    asOf
      ? `Quick preview from the ${asOf} refresh. Step 3 generates the exact copy.`
      : "Quick preview from the latest refresh. Step 3 generates the exact copy.",
} as const;

// ─── Distributions list ───────────────────────────────────────────────────────

export interface DistributionHowItWorksChip {
  label: string;
  /** Monospace chips show filter expressions such as `Region = West`. */
  mono?: boolean;
  icon?: "sparkles" | "mail" | "hash" | "clock";
}

export interface DistributionHowItWorksStep {
  number: number;
  title: string;
  body: string;
  chips: readonly DistributionHowItWorksChip[];
}

export interface DistributionExample {
  kicker: string;
  title: string;
  body: string;
  chips: readonly string[];
}

export const DISTRIBUTION_HOW_IT_WORKS = {
  title: "How personalized distribution works",
  steps: [
    {
      number: 1,
      title: "Split",
      body: "Pick a field in the story, such as Region. Each value becomes its own copy, filtered when the data is queried.",
      chips: [],
    },
    {
      number: 2,
      title: "Personalize",
      body: "Each copy shows only that slice’s numbers, with the recipient’s logo and colour and an AI summary of their data.",
      chips: [{ label: "AI summary", icon: "sparkles" }, { label: "Their logo" }],
    },
    {
      number: 3,
      title: "Deliver",
      body: "An email with a PDF or PowerPoint and key charts in the body, or a Slack post. Send now, once, or on a schedule.",
      chips: [
        { label: "Email", icon: "mail" },
        { label: "Slack", icon: "hash" },
        { label: "Weekly", icon: "clock" },
      ],
    },
  ] as readonly DistributionHowItWorksStep[],
  examples: [
    {
      kicker: "Example",
      title: "Quarterly business reviews",
      body: "A customer success manager keeps one QBR story. Every quarter, each client’s CIO gets a deck filtered to their own account, with their company’s logo.",
      chips: ["Split by Customer", "PowerPoint", "Quarterly"],
    },
    {
      kicker: "Example",
      title: "Regional sales pack",
      body: "A sales leader keeps one pipeline story. Every Monday, each regional VP gets their region’s numbers and nothing else.",
      chips: ["Split by Region", "PDF + Slack", "Weekly"],
    },
  ] as readonly DistributionExample[],
} as const;

/** One archive confirm for the list and the runs page. Archiving sets the status to archived (nothing is deleted); there is no Restore yet. */
export const DISTRIBUTION_ARCHIVE_COPY = {
  // DELETE archives (status "archived", schedule removed). It stays readable and can be restored from the list's Archived filter.
  message: "It stops all sends and leaves this list. Nothing is deleted: you can find it under Archived and restore it.",
  confirm: "Archive",
  cancel: "Cancel",
  close: "Close",
} as const;

export const DISTRIBUTION_LIST_COPY = {
  title: DISTRIBUTION_FEATURE_NAME,
  /** The story title sits between these two parts, in bold. */
  introBefore: "Each person gets their own copy of ",
  introAfter: ", filtered to their slice and sent on their channel.",
  newDistribution: "New distribution",
  emptyTitle: "No distributions for this story yet",
  emptyBody:
    "Set one up in a few minutes: pick a field to split by, upload who gets which slice, and choose when it goes out.",
  /** Small line under the empty state's steps. */
  emptyFootnote:
    "For example: a weekly review per regional lead, or a quarterly review per customer account.",
  footNote:
    "Last run shows the most recent run. Open a distribution to see every recipient.",
  loading: "Loading distributions…",
  loadError: "The distributions couldn’t be loaded",
  /** Tag after the server’s own message. */
  fromServer: "(from the server)",
  retry: "Try again",
  columns: {
    name: "Distribution",
    split: "Split by",
    recipients: "Recipients",
    schedule: "Schedule",
    nextRun: "Next run",
    lastResult: "Last result",
    status: "Status",
    actions: "Actions",
  },
  noneYet: "None yet",
  noValue: "—",
  paused: "Paused",
  notScheduled: "Not scheduled yet",
  sendOnDemand: "Send now only",
  resume: "Resume",
  count: (count: number) =>
    `${count} ${count === 1 ? "distribution" : "distributions"}`,
  copies: (count: number) => `${count} ${count === 1 ? "copy" : "copies"}`,
  actionsFor: (name: string) => `Actions for ${name}`,
  previewFor: (name: string) => `Preview what people get from ${name}`,
} as const;

export const DISTRIBUTION_ROW_ACTIONS_COPY = {
  pause: "Pause",
  pauseHint: "No copies go out until you resume",
  resume: "Resume",
  resumeHint: (next: string | null) =>
    next ? `Next run: ${next}` : "Copies go out on the schedule again",
  edit: "Edit",
  resumeEditing: "Resume editing",
  runNow: "Run now",
  runNowHint: "Send every copy now",
  delete: "Delete",
} as const;

export const DISTRIBUTION_DELETE_COPY = {
  question: (name: string) => `Delete “${name}”?`,
  consequence:
    "Recipients stop getting it and its run history is removed. This can’t be undone.",
  keep: "Keep it",
  confirm: "Delete",
  deleting: "Deleting…",
} as const;

export const DISTRIBUTION_NOTICES = {
  paused: (name: string) =>
    `Paused “${name}”. No copies go out until you resume.`,
  resumed: (name: string) => `Resumed “${name}”.`,
  deleted: (name: string) => `Deleted “${name}”.`,
  runStarted: (name: string) => `Started a run of “${name}”.`,
  draftSaved: "Draft saved. Resume it any time from this list.",
  discarded: (name: string) => `Discarded your edits to “${name}”.`,
  discardedPeopleStay: (name: string) => `Discarded your edits to “${name}”. Your changes to the list of people stay.`,
  pauseFailed: "We couldn’t pause this distribution. Try again.",
  resumeFailed: "We couldn’t resume this distribution. Try again.",
  deleteFailed: "We couldn’t delete this distribution. Try again.",
  runFailed: "We couldn’t start a run. Try again.",
  nothingToPreview: (name: string) =>
    `“${name}” hasn’t sent anything yet, so there’s no copy to preview. Open it and use the preview in step 3.`,
  noCopyInLastRun: (name: string) =>
    `The last run of “${name}” made no copies to preview. Open the run to see why.`,
  previewFailed: "We couldn’t open a copy to preview. Try again.",
} as const;

// ─── Story Share menu ─────────────────────────────────────────────────────────

export const DISTRIBUTION_SHARE_ENTRY = {
  value: "personalized_distribution",
  label: DISTRIBUTION_FEATURE_NAME,
  description: "Send each person only their slice, on a schedule.",
  badge: "New",
} as const;
